import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material'
import { IconAlertTriangle } from '@tabler/icons-react'
import { FormattedMessage } from 'react-intl'
import { useDispatch } from '../../../../store/store'
import { setTriggerOrdersRefetch } from '../../../../store/slices/general'
import axiosInstance from '../../../../utils/axiosInstance'

interface Props {
  open: boolean
  orderId: string
  onConfirm: () => void
  onCancel: () => void
}

const DeleteOrderModal: React.FC<Props> = ({ open, orderId, onConfirm, onCancel }) => {
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const dispatch = useDispatch()

  const handleDelete = async () => {
    setIsDeleting(true)
    setError(null)

    try {
      const response = await axiosInstance.delete(`/orders/${orderId}`)

      if (response.data?.success) {
        // Trigger orders table refresh
        dispatch(setTriggerOrdersRefetch(true))
        onConfirm()
      } else {
        setError('Failed to delete order. Please try again.')
      }
    } catch (err: any) {
      console.error('Error deleting order:', err)

      if (err.response?.status === 403) {
        setError('You do not have permission to delete orders.')
      } else if (err.response?.status === 404) {
        setError('Order not found.')
      } else {
        setError(err.response?.data?.error || 'Failed to delete order. Please try again.')
      }
    } finally {
      setIsDeleting(false)
    }
  }

  const handleCancel = () => {
    setError(null)
    onCancel()
  }

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconAlertTriangle color="#f44336" size="24px" />
        <FormattedMessage id="delete-order-title" defaultMessage="Delete Order" />
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          <FormattedMessage
            id="delete-order-confirmation"
            defaultMessage="Are you sure you want to delete this order?"
          />
        </Typography>

        <Typography variant="body2" color="text.secondary">
          <FormattedMessage
            id="delete-order-warning"
            defaultMessage="This will permanently remove the order record from the system."
          />
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancel} variant="outlined" disabled={isDeleting}>
          <FormattedMessage id="cancel" defaultMessage="Cancel" />
        </Button>

        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          disabled={isDeleting}
          startIcon={isDeleting ? <CircularProgress size={16} /> : null}
        >
          {isDeleting ? (
            <FormattedMessage id="deleting" defaultMessage="Deleting..." />
          ) : (
            <FormattedMessage id="delete-order" defaultMessage="Delete Order" />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DeleteOrderModal
